/**
 * Minimal i18n. Language dictionaries with fallback to English.
 * Persists selection to localStorage.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";

const en = {
  app_name: "Fender",
  by_line: "by SparesHub",
  // nav
  nav_dashboard: "Dashboard",
  nav_my_jobs: "My Jobs",
  nav_job_cards: "Job Cards",
  nav_customers: "Customers",
  nav_vehicles: "Vehicles",
  nav_inventory: "Inventory",
  nav_enquiries: "SparesHub Enquiries",
  nav_invoices: "Invoices",
  nav_users: "Team",
  nav_pickup_drop: "Pickup / Drop",
  nav_driver_day: "My Day",
  nav_reports: "Reports",
  // common
  search_placeholder: "Search customers, vehicles, job cards…",
  sign_out: "Sign out",
  sign_in: "Sign in",
  save: "Save",
  cancel: "Cancel",
  add: "Add",
  new: "New",
  submit: "Submit",
  status: "Status",
  today: "Today",
  language: "Language",
  // dashboard
  dash_header: "Today at the shop",
  kpi_open_jobs: "Open Job Cards",
  kpi_checkins: "Today's Check-ins",
  kpi_revenue: "Revenue Today (₹)",
  kpi_outstanding: "Outstanding (₹)",
  kpi_lowstock: "Low-stock Parts",
  kpi_enquiries: "Pending Enquiries",
  kpi_completed: "Completed",
  kpi_alerts: "Alerts",
  // job card statuses
  st_open: "Open",
  st_in_progress: "In Progress",
  st_awaiting_approval: "Awaiting Approval",
  st_awaiting_parts: "Awaiting Parts",
  st_completed: "Completed",
  st_delivered: "Delivered",
  st_cancelled: "Cancelled",
  // job card actions
  jc_start: "Start",
  jc_pause: "Pause",
  jc_complete: "Complete",
  jc_download_pdf: "Download PDF",
  jc_share_wa: "Share on WhatsApp",
  jc_customer_link: "Copy customer status link",
  jc_customer_link_copied: "Customer status link copied",
  // pickup drop
  pd_schedule: "Schedule Pickup / Drop",
  pd_type_pickup: "Pickup",
  pd_type_drop: "Drop",
  pd_address: "Address",
  pd_scheduled_at: "Scheduled at",
  pd_driver: "Driver",
  pd_notes: "Notes",
  pd_open_maps: "Open in Maps",
  pd_call: "Call",
  pd_status_scheduled: "Scheduled",
  pd_status_en_route: "En route",
  pd_status_arrived: "Arrived",
  pd_status_completed: "Completed",
  pd_status_cancelled: "Cancelled",
  driver_day_title: "My Day",
  driver_day_empty: "No pickups or drops assigned to you today.",
  // reports
  reports_title: "Reports",
  rep_revenue: "Revenue",
  rep_mechanics: "Mechanics",
  rep_parts: "Parts",
  rep_export_csv: "Export CSV",
  // customer portal
  portal_title: "Service status",
  portal_thanks: "Thanks for choosing us — here's how your vehicle is progressing.",
  portal_tasks: "Work items",
  portal_estimate: "Estimate",
  portal_invoice: "Invoice",
};

const hi = {
  ...en,
  nav_dashboard: "डैशबोर्ड",
  nav_my_jobs: "मेरे काम",
  nav_job_cards: "जॉब कार्ड",
  nav_customers: "ग्राहक",
  nav_vehicles: "वाहन",
  nav_inventory: "इन्वेंट्री",
  nav_enquiries: "स्पेयर पूछताछ",
  nav_invoices: "इनवॉइस",
  nav_users: "टीम",
  nav_pickup_drop: "पिकअप / ड्रॉप",
  nav_driver_day: "मेरा दिन",
  nav_reports: "रिपोर्ट",
  search_placeholder: "ग्राहक, वाहन, जॉब कार्ड खोजें…",
  sign_out: "साइन आउट",
  sign_in: "साइन इन",
  save: "सहेजें",
  cancel: "रद्द करें",
  add: "जोड़ें",
  new: "नया",
  submit: "जमा करें",
  status: "स्थिति",
  today: "आज",
  language: "भाषा",
  dash_header: "आज की स्थिति",
  kpi_open_jobs: "खुले जॉब कार्ड",
  kpi_checkins: "आज के चेक-इन",
  kpi_revenue: "आज का राजस्व (₹)",
  kpi_outstanding: "बकाया (₹)",
  kpi_lowstock: "कम स्टॉक पार्ट्स",
  kpi_enquiries: "लंबित पूछताछ",
  kpi_completed: "पूर्ण",
  kpi_alerts: "अलर्ट",
  st_open: "खुला",
  st_in_progress: "चल रहा है",
  st_awaiting_approval: "मंज़ूरी बाकी",
  st_awaiting_parts: "पार्ट्स की प्रतीक्षा",
  st_completed: "पूर्ण",
  st_delivered: "डिलीवर",
  st_cancelled: "रद्द",
  jc_start: "शुरू करें",
  jc_pause: "रोकें",
  jc_complete: "पूरा करें",
  jc_download_pdf: "PDF डाउनलोड",
  jc_share_wa: "व्हाट्सएप पर भेजें",
  jc_customer_link: "ग्राहक स्टेटस लिंक कॉपी करें",
  jc_customer_link_copied: "ग्राहक लिंक कॉपी हो गया",
  pd_schedule: "पिकअप / ड्रॉप शेड्यूल करें",
  pd_type_pickup: "पिकअप",
  pd_type_drop: "ड्रॉप",
  pd_address: "पता",
  pd_scheduled_at: "समय",
  pd_driver: "ड्राइवर",
  pd_notes: "नोट्स",
  pd_open_maps: "मैप में खोलें",
  pd_call: "कॉल",
  pd_status_scheduled: "शेड्यूल",
  pd_status_en_route: "रास्ते में",
  pd_status_arrived: "पहुँच गया",
  pd_status_completed: "पूर्ण",
  pd_status_cancelled: "रद्द",
  driver_day_title: "मेरा दिन",
  driver_day_empty: "आज आपके लिए कोई पिकअप/ड्रॉप नहीं है।",
  reports_title: "रिपोर्ट",
  rep_revenue: "राजस्व",
  rep_mechanics: "मैकेनिक",
  rep_parts: "पार्ट्स",
  rep_export_csv: "CSV एक्सपोर्ट",
  portal_title: "सर्विस स्टेटस",
  portal_thanks: "हमें चुनने के लिए धन्यवाद — आपके वाहन की स्थिति नीचे दी गई है।",
  portal_tasks: "कार्य",
  portal_estimate: "एस्टीमेट",
  portal_invoice: "इनवॉइस",
};

const kn = {
  ...en,
  nav_dashboard: "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
  nav_my_jobs: "ನನ್ನ ಕೆಲಸಗಳು",
  nav_job_cards: "ಜಾಬ್ ಕಾರ್ಡ್‌ಗಳು",
  nav_customers: "ಗ್ರಾಹಕರು",
  nav_vehicles: "ವಾಹನಗಳು",
  nav_inventory: "ದಾಸ್ತಾನು",
  nav_enquiries: "ಸ್ಪೇರ್ ವಿಚಾರಣೆ",
  nav_invoices: "ಬಿಲ್‌ಗಳು",
  nav_users: "ತಂಡ",
  nav_pickup_drop: "ಪಿಕಪ್ / ಡ್ರಾಪ್",
  nav_driver_day: "ನನ್ನ ದಿನ",
  nav_reports: "ವರದಿಗಳು",
  search_placeholder: "ಗ್ರಾಹಕ, ವಾಹನ, ಜಾಬ್ ಕಾರ್ಡ್ ಹುಡುಕಿ…",
  sign_out: "ಸೈನ್ ಔಟ್",
  sign_in: "ಸೈನ್ ಇನ್",
  save: "ಉಳಿಸಿ",
  add: "ಸೇರಿಸಿ",
  new: "ಹೊಸದು",
  submit: "ಸಲ್ಲಿಸಿ",
  today: "ಇಂದು",
  language: "ಭಾಷೆ",
  dash_header: "ಇಂದಿನ ಕೆಲಸಗಳು",
  kpi_open_jobs: "ತೆರೆದ ಜಾಬ್ ಕಾರ್ಡ್‌ಗಳು",
  kpi_checkins: "ಇಂದಿನ ಚೆಕ್-ಇನ್",
  kpi_revenue: "ಇಂದಿನ ಆದಾಯ (₹)",
  kpi_outstanding: "ಬಾಕಿ (₹)",
  kpi_lowstock: "ಕಡಿಮೆ ಸ್ಟಾಕ್",
  st_open: "ತೆರೆದ",
  st_in_progress: "ನಡೆಯುತ್ತಿದೆ",
  st_awaiting_approval: "ಅನುಮೋದನೆಗಾಗಿ",
  st_completed: "ಮುಗಿದಿದೆ",
  st_delivered: "ವಿತರಣೆ",
  jc_start: "ಆರಂಭಿಸಿ",
  jc_pause: "ವಿರಾಮ",
  jc_complete: "ಪೂರ್ಣಗೊಳಿಸಿ",
  jc_download_pdf: "PDF ಡೌನ್‌ಲೋಡ್",
  jc_share_wa: "ವಾಟ್ಸಾಪ್‌ನಲ್ಲಿ ಹಂಚಿಕೊಳ್ಳಿ",
  pd_type_pickup: "ಪಿಕಪ್",
  pd_type_drop: "ಡ್ರಾಪ್",
  pd_open_maps: "ಮ್ಯಾಪ್‌ನಲ್ಲಿ ತೆರೆಯಿರಿ",
  pd_call: "ಕರೆ",
  driver_day_title: "ನನ್ನ ದಿನ",
  reports_title: "ವರದಿಗಳು",
  rep_revenue: "ಆದಾಯ",
  rep_mechanics: "ಮೆಕ್ಯಾನಿಕ್",
  rep_parts: "ಪಾರ್ಟ್ಸ್",
  rep_export_csv: "CSV ಎಕ್ಸ್‌ಪೋರ್ಟ್",
  portal_title: "ಸೇವಾ ಸ್ಥಿತಿ",
  portal_thanks: "ನಮ್ಮನ್ನು ಆಯ್ಕೆ ಮಾಡಿದ್ದಕ್ಕಾಗಿ ಧನ್ಯವಾದಗಳು — ನಿಮ್ಮ ವಾಹನದ ಸ್ಥಿತಿ ಇಲ್ಲಿದೆ.",
  portal_tasks: "ಕೆಲಸಗಳು",
  portal_estimate: "ಅಂದಾಜು",
  portal_invoice: "ಬಿಲ್",
};

const ta = {
  ...en,
  nav_dashboard: "டாஷ்போர்டு",
  nav_my_jobs: "என் வேலைகள்",
  nav_job_cards: "வேலை அட்டைகள்",
  nav_customers: "வாடிக்கையாளர்கள்",
  nav_vehicles: "வாகனங்கள்",
  nav_inventory: "பொருள் வைப்பு",
  nav_enquiries: "பாகங்கள் விசாரணை",
  nav_invoices: "இன்வாய்ஸ்",
  nav_users: "குழு",
  nav_pickup_drop: "பிக்கப் / டிராப்",
  nav_driver_day: "என் நாள்",
  nav_reports: "அறிக்கைகள்",
  search_placeholder: "வாடிக்கையாளர், வாகனம், வேலை அட்டை தேடு…",
  sign_out: "வெளியேறு",
  sign_in: "உள்நுழை",
  save: "சேமி",
  add: "சேர்க்க",
  new: "புதிய",
  today: "இன்று",
  language: "மொழி",
  dash_header: "இன்றைய நிலை",
  kpi_open_jobs: "திறந்த வேலை அட்டைகள்",
  kpi_revenue: "இன்றைய வருவாய் (₹)",
  kpi_outstanding: "நிலுவை (₹)",
  st_open: "திறந்த",
  st_in_progress: "நடந்து கொண்டிருக்கிறது",
  st_completed: "முடிந்தது",
  st_delivered: "டெலிவர்ட்",
  jc_start: "தொடங்கு",
  jc_pause: "இடைநிறுத்து",
  jc_complete: "முடி",
  jc_download_pdf: "PDF பதிவிறக்கு",
  jc_share_wa: "வாட்ஸ்அப்பில் பகிர்",
  pd_type_pickup: "பிக்கப்",
  pd_type_drop: "டிராப்",
  pd_open_maps: "வரைபடத்தில் திற",
  pd_call: "அழை",
  driver_day_title: "என் நாள்",
  reports_title: "அறிக்கைகள்",
  rep_revenue: "வருவாய்",
  rep_mechanics: "மெக்கானிக்",
  rep_parts: "பாகங்கள்",
  rep_export_csv: "CSV ஏற்றுமதி",
  portal_title: "சேவை நிலை",
  portal_thanks: "எங்களை தேர்வு செய்ததற்கு நன்றி — உங்கள் வாகனத்தின் நிலை கீழே.",
  portal_tasks: "வேலைகள்",
};

const ml = {
  ...en,
  nav_dashboard: "ഡാഷ്ബോർഡ്",
  nav_my_jobs: "എന്റെ ജോലികൾ",
  nav_job_cards: "ജോബ് കാർഡുകൾ",
  nav_customers: "കസ്റ്റമേഴ്‌സ്",
  nav_vehicles: "വാഹനങ്ങൾ",
  nav_inventory: "ഇൻവെന്ററി",
  nav_enquiries: "പാർട്ട്‌സ് അന്വേഷണം",
  nav_invoices: "ഇൻവോയ്‌സ്",
  nav_users: "ടീം",
  nav_pickup_drop: "പിക്കപ്പ് / ഡ്രോപ്പ്",
  nav_driver_day: "എന്റെ ദിവസം",
  nav_reports: "റിപ്പോർട്ടുകൾ",
  search_placeholder: "കസ്റ്റമർ, വാഹനം, ജോബ് കാർഡ് തിരയുക…",
  sign_out: "സൈൻ ഔട്ട്",
  sign_in: "സൈൻ ഇൻ",
  save: "സേവ്",
  add: "ചേർക്കുക",
  today: "ഇന്ന്",
  language: "ഭാഷ",
  dash_header: "ഇന്നത്തെ അവസ്ഥ",
  kpi_open_jobs: "തുറന്ന ജോബ് കാർഡുകൾ",
  kpi_revenue: "ഇന്നത്തെ വരുമാനം (₹)",
  st_open: "തുറന്നത്",
  st_in_progress: "പുരോഗതിയിൽ",
  st_completed: "പൂർത്തിയായി",
  st_delivered: "ഡെലിവർ ചെയ്തു",
  jc_start: "ആരംഭിക്കുക",
  jc_pause: "വിരാമം",
  jc_complete: "പൂർത്തിയാക്കുക",
  jc_download_pdf: "PDF ഡൗൺലോഡ്",
  jc_share_wa: "വാട്ട്‌സ്ആപ്പിൽ പങ്കിടുക",
  pd_type_pickup: "പിക്കപ്പ്",
  pd_type_drop: "ഡ്രോപ്പ്",
  pd_open_maps: "മാപ്പിൽ തുറക്കുക",
  pd_call: "വിളിക്കുക",
  driver_day_title: "എന്റെ ദിവസം",
  reports_title: "റിപ്പോർട്ടുകൾ",
  rep_revenue: "വരുമാനം",
  rep_mechanics: "മെക്കാനിക്ക്",
  rep_parts: "പാർട്ട്‌സ്",
  rep_export_csv: "CSV എക്‌സ്‌പോർട്ട്",
  portal_title: "സേവന നില",
  portal_thanks: "ഞങ്ങളെ തിരഞ്ഞെടുത്തതിന് നന്ദി — വാഹനത്തിന്റെ അവസ്ഥ ചുവടെ.",
  portal_tasks: "ജോലികൾ",
};

const DICTS = { en, hi, kn, ta, ml };

export const LANGUAGES = [
  { code: "en", label: "English",       native: "English" },
  { code: "hi", label: "हिन्दी",         native: "Hindi" },
  { code: "mr", label: "मराठी",          native: "Marathi" },
  { code: "kn", label: "ಕನ್ನಡ",          native: "Kannada" },
  { code: "ta", label: "தமிழ்",          native: "Tamil" },
  { code: "te", label: "తెలుగు",         native: "Telugu" },
  { code: "gu", label: "ગુજરાતી",        native: "Gujarati" },
  { code: "ml", label: "മലയാളം",         native: "Malayalam" },
];

const LangContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("fender_lang") || "en");
  const setLang = (l) => {
    localStorage.setItem("fender_lang", l);
    setLangState(l);
  };
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);
  const t = useMemo(() => {
    const dict = DICTS[lang] || en;
    return (key) => dict[key] || en[key] || key;
  }, [lang]);
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
};

export const useI18n = () => useContext(LangContext) || { lang: "en", setLang: () => {}, t: (k) => en[k] || k };
